import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import { ECOMMERCE_RZP_KEY, ECOMMERCE_RZP_CREATE_ORDER, ECOMMERCE_RZP_VERIFY } from '../../config/api_path';
import './RazorpayPaymentModal.css';

const RazorpayPaymentModal = ({ isOpen, onClose, amount, orderId, onSuccess, onError }) => {
  const [loading, setLoading] = useState(false);
  const [razorpayKey, setRazorpayKey] = useState(null);
  const [timer, setTimer] = useState(12 * 60); // 12 minutes in seconds
  const timerIntervalRef = useRef(null);

  useEffect(() => {
    if (isOpen && !razorpayKey) {
      fetchRazorpayKey();
    }
    if (isOpen) {
      // Reset timer when modal opens
      setTimer(12 * 60);
      // Start countdown timer
      timerIntervalRef.current = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            clearInterval(timerIntervalRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [isOpen]);

  const formatTimer = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const fetchRazorpayKey = async () => {
    try {
      const response = await api.get(ECOMMERCE_RZP_KEY);
      if (response.data.success) {
        setRazorpayKey(response.data.data.keyId);
      } else {
        onError('Failed to load Razorpay configuration');
      }
    } catch (error) {
      console.error('Error fetching Razorpay key:', error);
      onError('Failed to load Razorpay configuration');
    }
  };

  const handlePayment = async () => {
    if (!razorpayKey) {
      onError('Razorpay not configured');
      return;
    }

    try {
      setLoading(true);

      // Create Razorpay order
      const orderResponse = await api.post(ECOMMERCE_RZP_CREATE_ORDER, {
        amount: amount,
        currency: 'INR',
        orderId: orderId
      });

      if (!orderResponse.data.success) {
        throw new Error('Failed to create payment order');
      }

      const razorpayOrder = orderResponse.data.data;

      // Razorpay options - configured for all payment methods
      const options = {
        key: razorpayKey,
        amount: razorpayOrder.amount,
        currency: razorpayOrder.currency,
        name: 'E-Bharat',
        description: `Order Payment - ${orderId ? `Order #${orderId}` : 'Checkout'}`,
        order_id: razorpayOrder.id,
        // Explicitly enable all payment methods including UPI (PhonePe, Google Pay, Paytm, BHIM)
        // Test mode OTP flow works automatically with test keys (rzp_test_...)
        method: {
          upi: true, // Enable UPI payment option (PhonePe, Google Pay, Paytm, BHIM)
          card: true, // Enable cards (Visa, Mastercard, RuPay)
          netbanking: true, // Enable net banking (all Indian banks)
          wallet: true, // Enable wallets (Mobikwik, etc.)
          emi: true, // Enable EMI
          paylater: true // Enable pay later
        },
        handler: async function (response) {
          try {
            // Verify payment
            const verifyResponse = await api.post(ECOMMERCE_RZP_VERIFY, {
              orderId: orderId,
              paymentId: response.razorpay_payment_id,
              signature: response.razorpay_signature,
              razorpayOrderId: response.razorpay_order_id
            });

            if (verifyResponse.data.success) {
              onSuccess(verifyResponse.data.data);
              onClose();
            } else {
              onError(verifyResponse.data.message || 'Payment verification failed');
            }
          } catch (error) {
            console.error('Error verifying payment:', error);
            onError(error.response?.data?.message || 'Payment verification failed');
          } finally {
            setLoading(false);
          }
        },
        prefill: {
          name: '',
          email: '',
          contact: ''
        },
        notes: {
          orderId: orderId || 'checkout'
        },
        theme: {
          color: '#1a5f3a' // Dark green to match E-Bharat branding
        },
        modal: {
          ondismiss: function() {
            setLoading(false);
            onClose();
          }
        }
      };

      // Initialize Razorpay
      if (window.Razorpay) {
        const razorpay = new window.Razorpay(options);
        razorpay.open();
      } else {
        throw new Error('Razorpay SDK not loaded');
      }
    } catch (error) {
      console.error('Error initiating payment:', error);
      onError(error.response?.data?.message || error.message || 'Failed to initiate payment');
      setLoading(false);
    }
  };

  // Generate a simple QR code placeholder (in production, this would be generated by Razorpay)
  const generateQRCode = () => {
    // This is a placeholder - in real implementation, Razorpay will generate the QR code
    // For now, we'll create a simple pattern that looks like a QR code
    return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2ZmZiIvPjxyZWN0IHg9IjEwIiB5PSIxMCIgd2lkdGg9IjE4MCIgaGVpZ2h0PSIxODAiIGZpbGw9IiMwMDAiLz48cmVjdCB4PSIyMCIgeT0iMjAiIHdpZHRoPSIxNjAiIGhlaWdodD0iMTYwIiBmaWxsPSIjZmZmIi8+PC9zdmc+';
  };

  if (!isOpen) return null;

  return (
    <div className="razorpay-modal-overlay" onClick={onClose}>
      <div className="razorpay-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header with E-Bharat branding */}
        <div className="razorpay-modal-header">
          <div className="ebharat-branding">
            <div className="ebharat-logo">
              <span className="ebharat-e">E</span>
            </div>
            <span className="ebharat-text">E-Bharat</span>
          </div>
          <div className="header-controls">
            <button className="refresh-btn" onClick={() => window.location.reload()}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 2V6M8 10V14M2 8H6M10 8H14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
            <button className="razorpay-modal-close" onClick={onClose}>&times;</button>
          </div>
        </div>
        
        <div className="razorpay-modal-body">
          {/* Pay With UPI QR Section */}
          <div className="payment-section upi-qr-section">
            <h3 className="section-title">Pay With UPI QR</h3>
            <div className="qr-container">
              <div className="qr-code-wrapper">
                <img 
                  src={generateQRCode()} 
                  alt="UPI QR Code" 
                  className="qr-code"
                />
              </div>
              <div className="qr-info">
                <p className="qr-instruction">Scan the QR using any UPI app on your phone.</p>
                <div className="upi-apps">
                  <div className="upi-app-logo google-pay">G</div>
                  <div className="upi-app-logo phonepe">पे</div>
                  <div className="upi-app-logo paytm">PAYTM</div>
                  <div className="upi-app-logo bhim">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M8 4L16 12L8 20V4Z" fill="currentColor"/>
                    </svg>
                  </div>
                </div>
                <p className="qr-timer">QR Code is valid for {formatTimer(timer)} minutes</p>
              </div>
            </div>
          </div>

          {/* Separator */}
          <div className="section-divider"></div>

          {/* Cards, UPI & More Section */}
          <div className="payment-section">
            <h3 className="section-title">Cards, UPI & More</h3>
            
            {/* Card Option */}
            <div className="payment-option" onClick={handlePayment}>
              <div className="payment-icon card-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
                  <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
              <span className="payment-option-text">Card</span>
              <div className="payment-logos">
                <span className="card-logo visa">VISA</span>
                <span className="card-logo mastercard">MC</span>
                <span className="card-logo rupay">RuPay</span>
              </div>
            </div>

            {/* UPI / QR Option */}
            <div className="payment-option" onClick={handlePayment}>
              <div className="payment-icon upi-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M8 5L16 12L8 19V5Z" fill="currentColor"/>
                </svg>
              </div>
              <span className="payment-option-text">UPI / QR</span>
              <div className="payment-logos">
                <div className="upi-app-logo-small google-pay">G</div>
                <div className="upi-app-logo-small phonepe">पे</div>
              </div>
            </div>

            {/* Netbanking Option */}
            <div className="payment-option" onClick={handlePayment}>
              <div className="payment-icon netbanking-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <rect x="4" y="4" width="16" height="16" rx="2" stroke="currentColor" strokeWidth="2"/>
                  <line x1="4" y1="8" x2="20" y2="8" stroke="currentColor" strokeWidth="2"/>
                  <line x1="8" y1="12" x2="16" y2="12" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
              <span className="payment-option-text">Netbanking</span>
              <span className="payment-subtext">All Indian banks</span>
            </div>

            {/* Wallet Option */}
            <div className="payment-option" onClick={handlePayment}>
              <div className="payment-icon wallet-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <rect x="3" y="6" width="18" height="12" rx="2" stroke="currentColor" strokeWidth="2"/>
                  <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
              <span className="payment-option-text">Wallet</span>
              <span className="payment-subtext">Mobikwik & more</span>
            </div>
          </div>

          {/* Footer with Amount and Pay Now Button */}
          <div className="razorpay-modal-footer">
            <div className="footer-amount">
              <div className="amount-display">₹{amount.toFixed(0)}</div>
              <button className="view-details-btn">View Details</button>
            </div>
            <button
              className="razorpay-pay-button"
              onClick={handlePayment}
              disabled={loading || !razorpayKey}
            >
              {loading ? 'Processing...' : 'Pay Now'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RazorpayPaymentModal;

